/**
 * @module Utils.externalUrl
 *
 * Opening a link a coach typed in themselves.
 *
 * A Booking Service Link and a Payment Service Link are free text on the coach's
 * profile, and four screens took them straight to `window.open`. Two things were
 * wrong with that. A value is not necessarily a web address — `javascript:` in
 * that field is script running on the page that opened it, which is a click away
 * from anyone viewing the profile. And "is this one of ours?" was asked with
 * `includes("coachnow.io")`, which reads the whole string, so a third party's
 * `coachnow.io.example.com` passed for our own domain and was swallowed by the
 * in-app flow instead of being opened.
 *
 * Parsing answers both, and it is the parsed pieces the questions are asked of:
 * the scheme for the first, the hostname for the second.
 */

const parse = (candidate: string): URL | null => {
  try {
    return new URL(candidate);
  } catch {
    return null;
  }
};

/**
 * The link as something safe to hand `window.open`, or `null` where there is
 * nothing safe to open.
 *
 * Only `http:` and `https:` survive. A value with no scheme of its own is read
 * as https — a coach typing "calendly.com/me" means a link, and passing that to
 * `window.open` as it came resolved it against the app and went nowhere.
 *
 * Open the result with `"noopener,noreferrer"`: `noopener` so the page cannot
 * reach back through `window.opener`, `noreferrer` so it is not told where it
 * came from.
 */
export const safeExternalUrl = (link?: string): string | null => {
  if (!link) {
    return null;
  }

  const trimmed = link.trim();
  if (trimmed === "") {
    return null;
  }

  // A scheme of its own is honoured — and then judged below.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    const absolute = parse(trimmed);
    return absolute && (absolute.protocol === "http:" || absolute.protocol === "https:")
      ? absolute.href
      : null;
  }

  /**
   * Without a scheme there are two shapes, and they are not the same thing.
   *
   * "//calendly.com/me" is protocol-relative: a host, under whatever scheme the
   * page is on, which here is https.
   *
   * "/book" is a path on the app's own origin, and nothing an external-link
   * button should be following. Stripping its slash to make a host out of it —
   * which is what this did — turned "/book" into `https://book/`, sending
   * someone to a domain of that name instead. There is no external link in a
   * value like that, so there is nothing to return.
   */
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return null;
  }

  const url = parse(`https://${trimmed.replace(/^\/+/, "")}`);

  if (!url) {
    return null;
  }

  return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
};

/**
 * Whether a link is CoachNow's own — `coachnow.io` and its subdomains, and
 * nothing that merely carries the name.
 *
 * Where the booking screens ask this, the answer decides between opening a tab
 * and staying in the app: one of our own links is the in-app flow written out
 * longhand, so it is booked in the app.
 */
export const isCoachNowUrl = (link?: string): boolean => {
  const url = link ? parse(link) : null;
  return url ? /^([^.]+\.)*coachnow\.io$/i.test(url.hostname) : false;
};

/**
 * The coach's own booking service, when that is where a "Book" button should
 * lead: a safe external link that is not one of ours. `null` means the caller's
 * own in-app booking flow.
 */
export const externalBookingUrl = (link?: string): string | null => {
  const url = safeExternalUrl(link);
  return url && !isCoachNowUrl(url) ? url : null;
};
